import type { Feature } from "../models";
import type { Plan, Subscription } from "../models";
import type { SubscriptionUsage } from "../types";
import { SEED_FEATURES } from "./features.seed";
import { SEED_PLANS } from "./plans.seed";
import SEED_SUBSCRIPTIONS, { SEED_USAGE } from "./subscriptions.seed";

const USE_API = import.meta.env.VITE_ITAAS_USE_SUBSCRIPTION_API === "true";
const PERSIST_KEY = "itaas:subscription-store:v1";
const SEED_TENANT_IDS = new Set(SEED_SUBSCRIPTIONS.map((s) => s.tenantId));

interface PersistedSubscriptionSlice {
  subscriptions: Subscription[];
  usage: Record<string, SubscriptionUsage>;
}

function cloneSeedData() {
  return {
    plans: structuredClone(SEED_PLANS),
    features: structuredClone(SEED_FEATURES),
    subscriptions: structuredClone(SEED_SUBSCRIPTIONS),
    usage: structuredClone(SEED_USAGE),
  };
}

function loadPersistedSlice(): PersistedSubscriptionSlice | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PERSIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSubscriptionSlice;
    if (!Array.isArray(parsed.subscriptions) || typeof parsed.usage !== "object") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function savePersistedSlice(data: ReturnType<typeof cloneSeedData>): void {
  if (typeof window === "undefined") return;
  const slice: PersistedSubscriptionSlice = {
    subscriptions: data.subscriptions.filter(
      (s) => !SEED_TENANT_IDS.has(s.tenantId)
    ),
    usage: Object.fromEntries(
      Object.entries(data.usage).filter(([tenantId]) => !SEED_TENANT_IDS.has(tenantId))
    ),
  };
  window.localStorage.setItem(PERSIST_KEY, JSON.stringify(slice));
}

function hydrateStore(): ReturnType<typeof cloneSeedData> {
  const seed = cloneSeedData();
  const persisted = loadPersistedSlice();
  if (!persisted) return seed;

  const subIds = new Set(seed.subscriptions.map((s) => s.id));
  for (const subscription of persisted.subscriptions) {
    if (!subIds.has(subscription.id)) {
      seed.subscriptions.push(subscription);
      subIds.add(subscription.id);
    }
  }

  for (const [tenantId, usage] of Object.entries(persisted.usage)) {
    seed.usage[tenantId] = usage;
  }

  return seed;
}

let store = hydrateStore();

export function initSubscriptionStore(): void {
  store = cloneSeedData();
  savePersistedSlice(store);
}

export function shouldUseSubscriptionApi(): boolean {
  return USE_API;
}

export function getStorePlans(): Plan[] {
  return store.plans;
}

export function getStoreFeatures(): Feature[] {
  return store.features;
}

export function getStoreSubscriptions(): Subscription[] {
  return store.subscriptions;
}

export function getStoreUsage(tenantId: string): SubscriptionUsage {
  return (
    store.usage[tenantId] ?? {
      users: 0,
      employees: 0,
      storageGb: 0,
      apiCallsThisMonth: 0,
    }
  );
}

export function mutateSubscriptionStore(
  mutator: (data: typeof store) => void
): void {
  mutator(store);
  savePersistedSlice(store);
}

export { SEED_FEATURES, SEED_PLANS, SEED_SUBSCRIPTIONS, SEED_USAGE };
